import { loadStripe, Stripe } from '@stripe/stripe-js';

let _p: Promise<Stripe | null> | null = null;

/**
 * Resolve the Stripe publishable key without assuming the host bundler defines
 * a global `process`. Bundlers (rspack/webpack DefinePlugin, Vite define)
 * statically REPLACE the exact token `process.env.REACT_APP_STRIPE_PUBLIC_KEY`
 * with a string literal at build time. If a given build forgot to define it,
 * a bare `process.env.X` access throws `ReferenceError: process is not defined`
 * in the browser (rspack/Vite don't ship a `process` shim). We read the token
 * inside a try/catch and also fall back to a `window` global so a blank/missing
 * key degrades to an empty string (Stripe simply won't mount) instead of
 * crashing the whole page.
 */
function resolvePublishableKey(): string {
  // 1) The build-time-replaced token. This MUST be the exact bare expression
  //    `process.env.REACT_APP_STRIPE_PUBLIC_KEY` so the bundler's DefinePlugin
  //    (rspack/webpack) or Vite `define` statically replaces it with a string
  //    literal — any cast/optional-chaining around `process` defeats that
  //    textual match and leaves a real runtime `process` lookup that is either
  //    empty or throws. The try/catch only guards the case where a bundler
  //    DIDN'T define the token (bare `process` → ReferenceError); when it DID,
  //    the whole expression is already a literal and `process` never appears.
  try {
    const fromProcess = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
    if (fromProcess) return fromProcess;
  } catch {
    /* `process` is not defined in this bundle — fall through */
  }
  // 2) Optional runtime override: a host page (or the widget) may set
  //    window.__CB_STRIPE_PK__ without a rebuild.
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fromWindow = (globalThis as any)?.__CB_STRIPE_PK__;
    if (fromWindow) return fromWindow;
  } catch {
    /* ignore */
  }
  return '';
}

/**
 * Lazily loads (and memoizes) the Stripe.js instance using the publishable
 * key. The promise is created once and reused across every consuming surface
 * (marketplace, public booking page, widget) so Stripe.js is only fetched a
 * single time per page load.
 */
export function getStripe(): Promise<Stripe | null> {
  if (!_p) {
    _p = loadStripe(resolvePublishableKey());
  }
  return _p;
}
